<template>
  <div class="border border-[#DFDFDF] rounded-[20px] relative">
    <span
      class="absolute top-3 start-3 p-1 rounded-md bg-secondary text-white z-10"
      v-if="tour.is_private == 1"
    >
      {{ t("tour_card.private") }}
    </span>
    <div
      v-if="isAuthenticated"
      class="absolute top-3 end-3 z-10"
      @click.stop
    >
      <UIAnimatedHeart
        :is-liked="tour.is_liked || false"
        :likes-count="tour.likes_count || 0"
        :is-loading="isLoading"
        @click="handleLikeClick"
      />
    </div>
    <NuxtLink
      :to="
        tour.is_private
          ? localPath('/private-trips/' + tour.id.toString())
          : localPath('/group-trips/' + tour.id.toString())
      "
    >
      <div>
        <div class="w-full h-[300px]">
          <NuxtImg
            :src="tour.image_link"
            class="w-full rounded-t-[20px] h-[300px]"
            alt=""
          />
        </div>
        <div class="py-3 px-2">
          <small class="text-fourth flex items-center gap-3 mb-3">
            <span> {{ tour.number_of_days }} {{ t("tour_card.days") }} </span>
            <font-awesome
              v-if="tour.continent && tour.continent.name"
              :icon="['fas', 'circle']"
              class="text-[5px]"
            />
            <span v-if="tour.continent && tour.continent.name">
              {{ tour.continent.name }}
            </span>
          </small>
          <h5 class="text-2xl leading-7 font-medium mb-2">
            {{ tour.title }}
          </h5>
          <!-- Operated by information (hide if operator is admin) -->
          <p
            v-if="tour.operator_name && tour.creator_type !== 'admin'"
            class="text-fourth text-sm mb-2"
          >
            {{ t("tour_card.operated_by") }} {{ tour.operator_name }}
          </p>
          <!-- Start date and end date -->
          <p
            v-if="tour.start_date && tour.end_date && tour.is_private == 0"
            class="text-fourth text-sm mb-4"
          >
            {{ formattedStartDate }} {{ t("tour_card.to") }} {{ formattedEndDate }}
          </p>
          <p
            v-else
            class="text-fourth text-sm mb-4"
          >
            {{ tour.number_of_days }} {{ t("tour_card.days") }}
          </p>
          <h6 class="text-right">
            <small class="text-fourth">
              {{ t("tour_card.starting_from") }}
            </small>
            <TripPriceDisplay
              :current-price="tour.discounted_sharing_price || tour.sharing_price"
              :original-price="tour.original_sharing_price"
              :is-free-for-members="tour.free_for_members || false"
              price-class="font-medium"
            />
          </h6>
        </div>
      </div>
    </NuxtLink>
  </div>
</template>

<script setup lang="ts">
const { t, locale } = useI18n();
const localPath = useLocalePath();
const props = defineProps<{ tour: Trip }>();
const tour = ref(props.tour) as Ref<Trip>;

const token = useCookie("token");
const isAuthenticated = computed(() => !!token.value);
const isLoading = ref(false);
const { likeTrip, unlikeTrip } = useTripLike();

// Format start date
const formattedStartDate = computed(() => {
  if (!tour.value?.start_date) return "";
  return new Date(tour.value.start_date).toLocaleDateString(locale.value, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
});

// Format end date
const formattedEndDate = computed(() => {
  if (!tour.value?.end_date) return "";
  return new Date(tour.value.end_date).toLocaleDateString(locale.value, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
});

const handleLikeClick = async () => {
  if (isLoading.value) return;
  
  isLoading.value = true;
  const currentLiked = tour.value.is_liked || false;
  
  try {
    const result = currentLiked
      ? await unlikeTrip(tour.value.id)
      : await likeTrip(tour.value.id);
    
    if (result.success) {
      tour.value.is_liked = result.isLiked;
      tour.value.likes_count = result.likesCount;
    } else {
      console.error("Error:", result.error);
    }
  } catch (error) {
    console.error("Error toggling like:", error);
  } finally {
    isLoading.value = false;
  }
};
</script>
